/** 레거시 `sheetRowReader.cjs` 포팅 */
import * as XLSX from 'xlsx';

type SheetRange = {
  s: { r: number; c: number };
  e: { r: number; c: number };
};

export const resolveSheetRowRange = (sheet: XLSX.WorkSheet): SheetRange => {
  if (!sheet?.['!ref']) {
    return { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };
  }

  const ref = XLSX.utils.decode_range(sheet['!ref']);
  let maxR = -1;
  let maxC = -1;

  for (const key of Object.keys(sheet)) {
    if (key[0] === '!') continue;
    const { r, c } = XLSX.utils.decode_cell(key);
    if (r > maxR) maxR = r;
    if (c > maxC) maxC = c;
  }

  for (const merge of sheet['!merges'] ?? []) {
    if (merge.e.r > maxR) maxR = merge.e.r;
    if (merge.e.c > maxC) maxC = merge.e.c;
  }

  if (maxR < 0 || maxC < 0) {
    return { s: { r: 0, c: 0 }, e: { r: ref.e.r, c: ref.e.c } };
  }

  return { s: { r: 0, c: 0 }, e: { r: maxR, c: maxC } };
};

export const sheetToRowArrays = (
  sheet: XLSX.WorkSheet,
  options: { defval?: unknown; raw?: boolean } = {},
): unknown[][] => {
  const { defval = null, raw = false } = options;
  if (!sheet?.['!ref']) return [];

  const range = resolveSheetRowRange(sheet);
  return XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval,
    raw,
    range,
  });
};

export const workbookToSheetRowsMap = (
  workbook: XLSX.WorkBook,
  options: { defval?: unknown; raw?: boolean } = {},
): Record<string, unknown[][]> =>
  Object.fromEntries(
    (workbook.SheetNames ?? []).map((sheetName) => [
      sheetName,
      sheetToRowArrays(workbook.Sheets[sheetName], options),
    ]),
  );

export const workbookToSheetMergesMap = (
  workbook: XLSX.WorkBook,
): Record<string, XLSX.Range[]> =>
  Object.fromEntries(
    (workbook.SheetNames ?? []).map((sheetName) => [
      sheetName,
      workbook.Sheets[sheetName]?.['!merges'] ?? [],
    ]),
  );

export const fillVerticalMergedColumnValues = (
  rows: unknown[][] = [],
  merges: XLSX.Range[] = [],
  columnIndex: number,
  options: { dataStartRowIndex?: number } = {},
): unknown[][] => {
  const { dataStartRowIndex = 0 } = options;
  if (
    columnIndex == null ||
    !Number.isFinite(columnIndex) ||
    !merges.length
  ) {
    return rows;
  }

  for (const merge of merges) {
    const start = merge?.s;
    const end = merge?.e;
    if (!start || !end) continue;
    if (start.c !== end.c || start.c !== columnIndex) continue;
    if (start.r >= end.r) continue;

    const masterRow = rows[start.r];
    if (!Array.isArray(masterRow)) continue;

    const masterValue = masterRow[columnIndex];
    if (masterValue == null || masterValue === '') continue;

    for (let rowIndex = start.r + 1; rowIndex <= end.r; rowIndex += 1) {
      if (rowIndex < dataStartRowIndex) continue;
      if (!Array.isArray(rows[rowIndex])) rows[rowIndex] = [];
      const currentValue = rows[rowIndex][columnIndex];
      if (currentValue == null || currentValue === '') {
        rows[rowIndex][columnIndex] = masterValue;
      }
    }
  }

  return rows;
};

export const hasColumnMergesInLayoutArea = (
  merges: XLSX.Range[] = [],
  layout: {
    headerRowIndex?: number;
    headerRowCount?: number;
    dataStartRowIndex?: number;
    layoutMergeSampleRows?: number;
  } = {},
): boolean => {
  const {
    headerRowIndex = 0,
    headerRowCount = 1,
    dataStartRowIndex = headerRowIndex + headerRowCount,
    layoutMergeSampleRows = 3,
  } = layout;

  if (!merges.length) return false;

  const headerEndRow = headerRowIndex + headerRowCount - 1;
  const areaEndRow = Math.max(
    headerEndRow,
    dataStartRowIndex + layoutMergeSampleRows - 1,
  );

  for (const merge of merges) {
    const s = merge?.s;
    const e = merge?.e;
    if (!s || !e) continue;
    if (e.r < headerRowIndex || s.r > areaEndRow) continue;

    if (e.c > s.c && s.r <= headerEndRow && e.r >= headerRowIndex) return true;
    if (e.r > s.r && s.r <= headerEndRow && e.r >= dataStartRowIndex) {
      return true;
    }
    if (e.c > s.c && s.r >= dataStartRowIndex && s.r <= areaEndRow) return true;
    if (e.r > s.r && s.r >= dataStartRowIndex && s.r <= areaEndRow) return true;
  }

  return false;
};
